import React, { useEffect, useState, useMemo } from "react";
import Modal from "./plansModel";
import SideBar from "../SideBar";
import Navbar from "../Navbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEdit,
  faPlus,
  faTrash,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../../axios-client";

const Plans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("");
  const [selectedPlanForModal, setSelectedPlanForModal] = useState(null);
  const [deletePlans, setDeletePlans] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);
  const [isDeleteSingleModalOpen, setIsDeleteSingleModalOpen] = useState(false);

  const navigate = useNavigate();

  const categories = [
    { label: "All Plans", value: "All" },
    { label: "Starter", value: "Starter" },
    { label: "Premium", value: "Premium" },
    { label: "Enterprise", value: "Enterprise" },
  ];

  useEffect(() => {
    const adminToken = localStorage.getItem("Admin_Token");
    if (!adminToken) {
      navigate("/admin/login");
    }
  }, [navigate]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axiosClient.get("/plans/getall");
      if (response.data?.success && Array.isArray(response.data.plans)) {
        setPlans(response.data.plans);
      } else if (Array.isArray(response.data)) {
        setPlans(response.data);
      } else {
        setPlans([]);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const handleOpenCreateModal = () => {
    setModalType("create");
    setSelectedPlanForModal(null);
    setIsModalOpen(true);
  };

  const handleViewPlan = (plan) => {
    setModalType("view");
    setSelectedPlanForModal(plan);
    setIsModalOpen(true);
  };

  const handleEditPlan = (plan) => {
    setModalType("edit");
    setSelectedPlanForModal(plan);
    setIsModalOpen(true);
  };

  const handleDeleteSingleClick = (plan) => {
    setPlanToDelete(plan);
    setIsDeleteSingleModalOpen(true);
  };

  const confirmDeleteSingle = async () => {
    if (!planToDelete?._id) return;
    try {
      await axiosClient.delete(`/plans/delete/plan/${planToDelete._id}`);
      alert("Plan deleted successfully!");
      setIsDeleteSingleModalOpen(false);
      setPlanToDelete(null);
      fetchPlans();
    } catch (error) {
      console.error("Error deleting plan:", error);
      alert("Failed to delete plan.");
    }
  };

  const handleDeleteAll = async () => {
    try {
      await axiosClient.delete("/plans/deleteall");
      alert("All plans deleted successfully!");
      setDeletePlans(false);
      fetchPlans();
    } catch (error) {
      console.error("Error deleting all plans:", error);
      alert("Failed to delete all plans.");
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalType("");
    setSelectedPlanForModal(null);
  };

  const filteredPlans = useMemo(() => {
    return plans.filter((plan) => {
      // Category filter (instant)
      const categoryMatch =
        selectedCategory === "All" ||
        (plan.category &&
          plan.category.trim().toLowerCase() ===
            selectedCategory.trim().toLowerCase());

      // Search term filter
      const term = search.toLowerCase().trim();
      if (!term) return categoryMatch;

      const nameMatch = plan.name
        ? plan.name.toLowerCase().includes(term)
        : false;
      const catMatch = plan.category
        ? plan.category.toLowerCase().includes(term)
        : false;

      return categoryMatch && (nameMatch || catMatch);
    });
  }, [plans, selectedCategory, search]);

  return (
    <>
      <SideBar />
      <Navbar />
      <div className="p-8 min-h-screen bg-gray-50" style={{ paddingLeft: "17rem" }}>
        {/* Top Header Section with Title on Left and Create New Plan Button on Top Right */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Plan Management</h1>
            <p className="text-sm text-gray-500 mt-1">
              View, manage, edit and create pricing subscription plans
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleOpenCreateModal}
              className="bg-[#38a3a5] hover:bg-[#2c8385] text-white px-5 py-2.5 rounded-lg font-medium shadow-sm transition-all duration-200 flex items-center cursor-pointer text-sm"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Create New Plan
            </button>
            {plans.length > 0 && (
              <button
                onClick={() => setDeletePlans(true)}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2.5 rounded-lg font-medium shadow-sm transition-all duration-200 flex items-center cursor-pointer text-sm"
              >
                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                Delete All
              </button>
            )}
          </div>
        </div>

        {/* Filter Bar: Left side = Search Bar & Count; Right side = 4 Category Filter Buttons */}
        <div className="flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-4 mb-6 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
          {/* Left side: Search Box and Count */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="relative w-full sm:w-80">
              <input
                type="text"
                placeholder="Search by plan name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#38a3a5] focus:border-transparent transition"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-bold"
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="text-xs text-gray-500 font-medium whitespace-nowrap pl-1">
              Showing{" "}
              <span className="font-bold text-[#38a3a5]">
                {filteredPlans.length}
              </span>{" "}
              of {plans.length} plans
            </div>
          </div>

          {/* Right side: 4 Category Filter Buttons */}
          <div className="flex flex-wrap items-center gap-2">
            {categories.map((cat) => {
              const isActive = selectedCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 cursor-pointer ${
                    isActive
                      ? "bg-[#38a3a5] text-white shadow-sm ring-2 ring-[#38a3a5] ring-offset-1"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Plans Content Area (Populated in place of former blank space) */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center text-gray-500">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#38a3a5] border-t-transparent mb-3"></div>
            <p className="text-base font-medium">Loading subscription plans...</p>
          </div>
        ) : filteredPlans.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <div className="w-16 h-16 bg-[#e6f4f4] text-[#38a3a5] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
              !
            </div>
            <h3 className="text-lg font-bold text-gray-800 mb-1">
              {search || selectedCategory !== "All"
                ? `No plans found in ${selectedCategory !== "All" ? `"${selectedCategory}"` : ""} ${search ? `matching "${search}"` : ""}`
                : "No plans created yet"}
            </h3>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
              {search || selectedCategory !== "All"
                ? "Try clearing your search or switching to 'All Plans' to view other plans."
                : "Create subscription plans so users can view and subscribe to your market research packages."}
            </p>
            {search || selectedCategory !== "All" ? (
              <button
                onClick={() => {
                  setSearch("");
                  setSelectedCategory("All");
                }}
                className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium transition cursor-pointer mr-3"
              >
                Reset Filter
              </button>
            ) : null}
            <button
              onClick={handleOpenCreateModal}
              className="bg-[#38a3a5] hover:bg-[#2c8385] text-white px-5 py-2 rounded-lg font-medium shadow-sm transition inline-flex items-center text-sm cursor-pointer"
            >
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Create New Plan
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-left">
                <thead className="bg-[#f0f7f7]">
                  <tr>
                    <th className="py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      S.No
                    </th>
                    <th className="py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Plan Name
                    </th>
                    <th className="py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Billing Cycle
                    </th>
                    <th className="py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider">
                      Customizable
                    </th>
                    <th className="py-3.5 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wider text-center">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredPlans.map((plan, index) => (
                    <tr
                      key={plan._id || index}
                      className="hover:bg-gray-50/80 transition-colors"
                    >
                      <td className="py-3.5 px-4 text-sm text-gray-500 font-medium">
                        {index + 1}
                      </td>
                      <td className="py-3.5 px-4 text-sm font-semibold text-gray-900">
                        {plan.name || "Unnamed Plan"}
                      </td>
                      <td className="py-3.5 px-4 text-sm">
                        <span className="inline-block bg-teal-50 text-[#2c8385] border border-teal-200 px-2.5 py-0.5 rounded-full text-xs font-medium">
                          {plan.category || "General"}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-sm font-bold text-gray-800">
                        ${plan.price ?? 0}
                      </td>
                      <td className="py-3.5 px-4 text-sm text-gray-600 capitalize">
                        {plan.billingCycle || "Monthly"}
                      </td>
                      <td className="py-3.5 px-4 text-sm">
                        {plan.isCustomizable ? (
                          <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded text-xs font-medium">
                            Yes
                          </span>
                        ) : (
                          <span className="text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded text-xs font-medium">
                            No
                          </span>
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-sm text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => handleViewPlan(plan)}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-2.5 py-1.5 rounded-md text-xs font-medium transition flex items-center shadow-xs"
                            title="View Details"
                          >
                            <FontAwesomeIcon icon={faEye} className="mr-1" />
                            View
                          </button>
                          <button
                            onClick={() => handleEditPlan(plan)}
                            className="bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1.5 rounded-md text-xs font-medium transition flex items-center shadow-xs"
                            title="Edit Plan"
                          >
                            <FontAwesomeIcon icon={faEdit} className="mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteSingleClick(plan)}
                            className="bg-red-500 hover:bg-red-600 text-white px-2.5 py-1.5 rounded-md text-xs font-medium transition flex items-center shadow-xs"
                            title="Delete Plan"
                          >
                            <FontAwesomeIcon icon={faTrash} className="mr-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal for Create / View / Edit */}
        {isModalOpen && (
          <Modal
            type={modalType}
            selectedPlanData={selectedPlanForModal}
            onClose={handleCloseModal}
            onSuccess={fetchPlans}
          />
        )}

        {/* Confirmation Modal for Single Plan Deletion */}
        {isDeleteSingleModalOpen && planToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120]">
            <div className="bg-white rounded-xl p-6 max-w-sm w-full text-center shadow-2xl">
              <h3 className="text-lg font-bold text-gray-800 mb-2">Delete Plan</h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-gray-900">
                  "{planToDelete.name}"
                </span>
                ? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setIsDeleteSingleModalOpen(false);
                    setPlanToDelete(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteSingle}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                >
                  Confirm Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal for Delete All */}
        {deletePlans && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[120]">
            <div className="bg-white rounded-xl p-6 max-w-sm w-full text-center shadow-2xl">
              <h3 className="text-lg font-bold text-red-600 mb-2">Delete All Plans</h3>
              <p className="text-sm text-gray-600 mb-6">
                Warning: This will permanently delete <strong>all {plans.length} plans</strong> from the database. Are you sure?
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setDeletePlans(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAll}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm font-medium"
                >
                  Delete All
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Plans;
